/**
 * Cloud upsell resolver — turns the current account state into an upsell offer
 * (product, price label, CTA text) for cloud-related unlock prompts.
 *
 * Single source of truth so every cloud touchpoint (SaveMenu, Sidebar locked
 * state, Dashboard upsell card, Open dialog locked tab) shows the same price
 * and routes to the same checkout target.
 *
 * Canonical pricing (from billing.js):
 *   Lite Lifetime         — $49 one-time
 *   Pro Lifetime          — $70 one-time
 *   Cloud & AI add-on     — $4.99/mo  · cloud storage + AI for lifetime holders
 *
 * There is no standalone cloud plan — the upsell always points at one of these
 * products, chosen by current tier.
 */

import { upgradeProductFor, isLifetimeTier } from './billing';

export const CLOUD_PRODUCT_PRICE = {
  lite_lifetime: '$49',
  pro_lifetime: '$70',
  cloud_ai: '$4.99/mo',
};

const PRODUCT_LABEL = {
  lite_lifetime: 'Lite Lifetime',
  pro_lifetime: 'Pro Lifetime',
  cloud_ai: 'Cloud & AI add-on',
};

/**
 * Resolve the cloud upsell for an account.
 *
 * @param {Object} account - the auth hook's `account` object (or null)
 * @returns {Object} upsell - { hasCloud, canBuy, product, price, label, ctaShort, ctaLong, reason }
 *
 * - hasCloud:  true if this account already has cloud capacity — no upsell needed.
 * - canBuy:    true if there is a sensible product to offer.
 * - product:   'lite_lifetime' | 'pro_lifetime' | 'cloud_ai' | null
 * - price:     human-readable price string (e.g. '$70')
 * - label:     product display name ('Pro Lifetime', 'Cloud & AI add-on', …)
 * - ctaShort:  button copy for tight spaces (split button menu rows)
 * - ctaLong:   button copy for full-width cards (Dashboard upsell)
 * - reason:    short accessibility/tooltip text explaining why this product
 */
export function getCloudUpsell(account) {
  const hasCloud = (account?.capabilities?.cloud_bytes || 0) > 0;
  if (hasCloud) {
    return {
      hasCloud: true,
      canBuy: false,
      product: null,
      price: null,
      label: null,
      ctaShort: null,
      ctaLong: null,
      reason: 'Cloud already enabled on this account.',
    };
  }

  const tier = account?.tier || 'free';
  const product = upgradeProductFor(tier, 'cloud_bytes');
  if (!product || !CLOUD_PRODUCT_PRICE[product]) {
    return { hasCloud: false, canBuy: false, product: null, price: null, label: null, ctaShort: null, ctaLong: null, reason: 'No upgrade path available.' };
  }

  const price = CLOUD_PRODUCT_PRICE[product];
  const label = PRODUCT_LABEL[product];

  let ctaShort;
  let ctaLong;
  let reason;
  if (product === 'cloud_ai') {
    ctaShort = `Add cloud — ${price}`;
    ctaLong  = `Add cloud & AI — ${price}`;
    reason   = 'Lifetime license · add cloud storage and AI tools';
  } else {
    // Free user: cloud + AI come from the $4.99/mo add-on, which requires
    // owning a lifetime license — so the first step is getting a license.
    ctaShort = `Unlock cloud — ${price}`;
    ctaLong  = `Get ${label} — ${price}`;
    reason   = 'Cloud sync + AI are a $4.99/mo add-on, available with any lifetime license';
  }

  return {
    hasCloud: false,
    canBuy: true,
    product,
    price,
    label,
    ctaShort,
    ctaLong,
    reason,
    isAddon: product === 'cloud_ai',
    isLifetime: isLifetimeTier(tier),
  };
}

/**
 * Resolve the cloud capability state for an account, for UI branching.
 *
 *   'available'  — cloud is live on this account (capabilities.cloud_bytes > 0)
 *   'locked'     — signed in but no cloud capacity; show upsell
 *   'signed-out' — not signed in; show sign-in prompt
 *
 * SaveMenu / Sidebar / FileOpenDialog all use this to pick render mode.
 */
export function getCloudState(user, account) {
  if (!user) return 'signed-out';
  if ((account?.capabilities?.cloud_bytes || 0) > 0) return 'available';
  return 'locked';
}
